/*
  NAFCO App: behaviour for the app-style layer (looks in nafco-app.css, markup in
  snippets/nafco-app-shell.liquid).

  Everything here is progressive: if this file fails to load, the theme still works as before.
  Cart changes go through Shopify.actions.updateCart, the same path the theme's own buttons use,
  so the header bubble and the cart drawer stay in step with the steppers and the cart bar.
*/
(() => {
  'use strict';

  const d = document;
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const $$ = (selector, scope = d) => Array.from(scope.querySelectorAll(selector));

  /* ── search ─────────────────────────────────────────────────────────────── */

  function openSearch() {
    const modal = d.getElementById('search-modal');
    if (modal && typeof modal.showDialog === 'function') modal.showDialog();
    else window.location.href = (window.Theme?.routes?.search_url || '/search');
  }

  d.addEventListener('click', (event) => {
    if (event.target.closest('[data-nafco-search]')) {
      event.preventDefault();
      openSearch();
    }
  });

  d.addEventListener('keydown', (event) => {
    if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
    const typing = event.target.closest('input, textarea, select, [contenteditable="true"]');
    if (typing) return;
    event.preventDefault();
    openSearch();
  });

  // Rotating hints: real departments of this store, so every hint returns results
  const HINTS = [
    'vegetable seeds', 'grow bags', 'flower bulbs', 'succulents', 'planter pots',
    'drip irrigation', 'microgreens', 'organic manure', 'indoor plants', 'herb seeds',
  ];

  function startRotor() {
    const rotors = $$('[data-nafco-rotor]');
    if (!rotors.length || reduceMotion) return;
    let i = 0;
    setInterval(() => {
      if (d.hidden) return;
      i = (i + 1) % HINTS.length;
      for (const rotor of rotors) {
        rotor.classList.remove('is-entering');
        rotor.classList.add('is-leaving');
        setTimeout(() => {
          rotor.textContent = `“${HINTS[i]}”`;
          rotor.classList.remove('is-leaving');
          rotor.classList.add('is-entering');
        }, 260);
      }
    }, 2800);
  }

  /* ── cart state ─────────────────────────────────────────────────────────── */
  // One copy of the cart for the whole page. Reads are debounced and numbered, so a burst of
  // taps makes one request and a late answer never overwrites a newer one.

  let cart = null;
  let cartVersion = 0; // bumps each time fresh cart data is applied
  let refreshTimer = 0;
  let refreshSeq = 0;
  let refreshWaiters = [];

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currency || 'INR',
        maximumFractionDigits: cents % 100 ? 2 : 0,
      }).format(cents / 100);
    } catch {
      return `₹${(cents / 100).toFixed(0)}`;
    }
  };

  function refreshCart(delay = 150) {
    return new Promise((resolve) => {
      refreshWaiters.push(resolve);
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => runRefresh(0), delay);
    });
  }

  async function runRefresh(attempt) {
    const seq = ++refreshSeq;
    const waiters = refreshWaiters;
    refreshWaiters = [];
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      if (seq === refreshSeq) {
        const previousCount = cart ? cart.item_count : null;
        cart = data;
        cartVersion++;
        renderCart(previousCount);
      }
    } catch {
      // Busy (rate-limited) or offline: try again gently, keeping the callers waiting
      if (attempt < 2) {
        setTimeout(() => {
          refreshWaiters.push(...waiters);
          runRefresh(attempt + 1);
        }, 1200 * (attempt + 1));
        return;
      }
    }
    waiters.forEach((resolve) => resolve());
  }

  function renderCart(previousCount) {
    if (!cart) return;
    const count = cart.item_count;

    for (const badge of $$('[data-nafco-cart-badge]')) {
      const text = count > 99 ? '99+' : String(count);
      if (badge.textContent !== text) badge.textContent = text;
      badge.hidden = count === 0;
      if (previousCount !== null && count > previousCount) bump(badge, 'is-bumped');
    }

    for (const bar of $$('[data-nafco-cartbar]')) {
      bar.hidden = count === 0;
      const countEl = bar.querySelector('[data-nafco-cart-count]');
      const totalEl = bar.querySelector('[data-nafco-cart-total]');
      if (countEl) countEl.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
      if (totalEl) totalEl.textContent = formatMoney(cart.total_price, cart.currency);
      if (previousCount !== null && count !== previousCount) bump(bar, 'nafco-bump');
    }

    d.body.classList.toggle('nafco-cartbar-on', count > 0 && !!d.querySelector('[data-nafco-cartbar]'));

    const toastCount = toastEl && toastEl.querySelector('[data-nafco-toast-count]');
    if (toastCount && !toastEl.classList.contains('is-error')) {
      toastCount.textContent = `${count} ${count === 1 ? 'item' : 'items'} in cart`;
    }

    renderSteppers();
  }

  function bump(element, className) {
    element.classList.remove(className);
    void element.offsetWidth;
    element.classList.add(className);
  }

  function openCart() {
    const drawer = d.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
    else window.location.href = window.Theme?.routes?.cart_url || '/cart';
  }

  d.addEventListener('click', (event) => {
    if (event.target.closest('[data-nafco-open-cart]')) {
      event.preventDefault();
      openCart();
    }
  });

  // Any cart change anywhere on the page (ours or the theme's) ends in one fresh read.
  // Adds that came from the theme's own buttons also get the "Added to your cart" box.
  let ownCall = false;

  d.addEventListener('shopify:cart:lines-update', (event) => {
    const isOwn = ownCall;
    const action = event.action || (event.detail && event.detail.action);
    const promise = event.promise && typeof event.promise.then === 'function' ? event.promise : null;
    if (!promise) {
      refreshCart();
      return;
    }
    promise.then(
      (result) => {
        if (!isOwn && action === 'add' && !hasErrors(result)) showToast({});
        refreshCart();
      },
      () => refreshCart()
    );
  });
  d.addEventListener('cart:update', () => refreshCart());
  window.addEventListener('pageshow', (event) => { if (event.persisted) refreshCart(0); });

  function hasErrors(result) {
    const errors = result && (result.userErrors || result.errors);
    return Array.isArray(errors) && errors.length > 0;
  }

  // Every add and change goes through Shopify's cart API (the same path the theme's cart drawer
  // listens to). The classic /cart/add.js endpoint is rate-limited far sooner under fast tapping.
  async function cartUpdate(lines) {
    const actions = window.Shopify && window.Shopify.actions;
    if (!actions || typeof actions.updateCart !== 'function') throw new Error('unavailable');
    let pending;
    ownCall = true;
    try {
      pending = actions.updateCart({ lines });
    } finally {
      ownCall = false;
    }
    const result = await pending;
    if (hasErrors(result)) {
      const errors = result.userErrors || result.errors;
      throw new Error(errors[0].message || 'rejected');
    }
    return result;
  }

  /* ── ADD becomes a stepper at once; taps are merged into one cart request ── */

  // One entry per variant, shared by every card that shows it (a product can sit in several rails)
  const lineStates = new Map();

  const variantOf = (quickAdd) => quickAdd.querySelector('input[name="id"]')?.value || null;

  function linesFor(productId) {
    if (!cart) return [];
    return cart.items.filter((item) => String(item.product_id) === String(productId));
  }

  function renderSteppers() {
    for (const quickAdd of $$('product-card quick-add-component')) {
      const productLines = linesFor(quickAdd.dataset.productId);
      const quantity = productLines.reduce((sum, item) => sum + item.quantity, 0);

      if (quickAdd.dataset.quickAddButton !== 'add') {
        const choose = quickAdd.querySelector('.quick-add__button--choose');
        if (choose) {
          const want = quantity > 0 ? String(quantity) : undefined;
          if (choose.dataset.nafcoCount !== want) {
            if (want) choose.dataset.nafcoCount = want;
            else delete choose.dataset.nafcoCount;
          }
        }
        continue;
      }

      const variant = variantOf(quickAdd);
      const state = variant ? lineStates.get(variant) : null;
      if (state && cart) reconcile(variant, state);
      paintStepper(quickAdd, state ? state.target : quantity);
    }
  }

  // Bring a line's state in step with the cart, without ever dropping taps that are still queued.
  // Only cart data read after the line's last change counts (older snapshots are ignored).
  function reconcile(variant, state) {
    if (cartVersion < state.minVersion) return;
    const line = cart.items.find((item) => String(item.variant_id) === String(variant));
    if (!state.inflight) state.key = line ? line.key : null;
    const idle = !state.inflight && !state.timer && !state.dirty;
    if (!idle) return;
    if (state.target === state.confirmed) {
      state.confirmed = line ? line.quantity : 0;
      state.target = state.confirmed;
    } else {
      // A change is waiting and nothing is scheduled to send it: send it
      state.timer = setTimeout(() => flushLine(variant), 60);
    }
  }

  const keyFromCart = (variant) => {
    const line = cart && cart.items.find((item) => String(item.variant_id) === String(variant));
    return line ? line.key : null;
  };

  function paintStepper(quickAdd, quantity) {
    let stepper = quickAdd.querySelector('.nafco-stepper');
    if (quantity > 0) {
      if (!stepper) {
        stepper = buildStepper();
        quickAdd.appendChild(stepper);
      }
      const output = stepper.querySelector('output');
      const value = String(quantity);
      if (output.textContent !== value) output.textContent = value;
      if (!quickAdd.classList.contains('nafco-in-cart')) quickAdd.classList.add('nafco-in-cart');
    } else if (stepper || quickAdd.classList.contains('nafco-in-cart')) {
      if (stepper) stepper.remove();
      quickAdd.classList.remove('nafco-in-cart');
    }
  }

  function paintVariant(variant) {
    const state = lineStates.get(variant);
    for (const quickAdd of $$('product-card quick-add-component')) {
      if (variantOf(quickAdd) === variant) paintStepper(quickAdd, state ? state.target : 0);
    }
  }

  function buildStepper() {
    const stepper = d.createElement('div');
    stepper.className = 'nafco-stepper';
    stepper.setAttribute('role', 'group');
    stepper.setAttribute('aria-label', 'Quantity in cart');
    stepper.innerHTML =
      '<button type="button" data-step="-1" aria-label="Remove one">−</button>' +
      '<output aria-live="polite">1</output>' +
      '<button type="button" data-step="1" aria-label="Add one">+</button>';
    return stepper;
  }

  function changeQuantity(quickAdd, delta, origin) {
    const variant = variantOf(quickAdd);
    if (!variant) return;
    let state = lineStates.get(variant);
    if (!state) {
      const line = cart && cart.items.find((item) => String(item.variant_id) === String(variant));
      state = {
        key: line ? line.key : null,
        confirmed: line ? line.quantity : 0,
        target: line ? line.quantity : 0,
        timer: 0,
        inflight: false,
        dirty: false,
        minVersion: 0,
        title: quickAdd.dataset.productTitle || '',
      };
      lineStates.set(variant, state);
    }
    state.target = Math.max(0, state.target + delta);
    paintVariant(variant);
    if (delta > 0) {
      burst(origin);
      haptic();
    }
    clearTimeout(state.timer);
    // The first ADD goes out at once; further taps wait for a short pause and go out together
    const firstAdd = state.confirmed === 0 && !state.inflight;
    state.timer = setTimeout(() => flushLine(variant), firstAdd ? 0 : 320);
  }

  async function flushLine(variant) {
    const state = lineStates.get(variant);
    if (!state) return;
    state.timer = 0;
    if (state.inflight) {
      state.dirty = true;
      return;
    }
    const want = state.target;
    if (want === state.confirmed) return;

    state.inflight = true;
    const wasEmpty = state.confirmed === 0;
    try {
      if (state.key) {
        await cartUpdate([{ id: state.key, quantity: want }]);
      } else if (want > state.confirmed) {
        // Not in the cart yet (or its line is still unknown): add only the difference
        await cartUpdate([{ merchandiseId: `gid://shopify/ProductVariant/${variant}`, quantity: want - state.confirmed }]);
      } else {
        await refreshCart(0);
        state.key = keyFromCart(variant);
        if (!state.key) throw new Error('busy');
        await cartUpdate([{ id: state.key, quantity: want }]);
      }
      state.confirmed = want;
      if (want === 0) state.key = null; // the line is gone; a later tap adds a fresh one
      if (wasEmpty && want > 0) showToast({ title: state.title, adding: want });
    } catch (error) {
      state.target = state.confirmed;
      showToast({ error: true, message: error && error.message });
    } finally {
      state.inflight = false;
      state.minVersion = cartVersion + 1;
    }
    paintVariant(variant);
    await refreshCart(0);
    if (state.dirty || state.target !== state.confirmed) {
      state.dirty = false;
      clearTimeout(state.timer);
      state.timer = setTimeout(() => flushLine(variant), 60);
    }
  }

  // Cards: ADD and the stepper buttons (capture phase, so the theme's own add never runs twice)
  d.addEventListener('click', (event) => {
    const step = event.target.closest('.nafco-stepper [data-step]');
    const add = !step && event.target.closest('product-card quick-add-component[data-quick-add-button="add"] .quick-add__button--add');
    if (!step && !add) return;
    const quickAdd = (step || add).closest('quick-add-component');
    if (!quickAdd || !variantOf(quickAdd)) return;
    if (add && (add.disabled || !(window.Shopify && window.Shopify.actions))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    changeQuantity(quickAdd, step ? Number(step.dataset.step) : 1, step || add);
  }, true);

  // Product page, the options pop-up and the sticky bar: same fast path, no cart drawer
  d.addEventListener('click', async (event) => {
    const button = event.target.closest('button[name="add"]');
    if (!button || button.disabled || button.closest('product-card')) return;
    const form = button.form || button.closest('form');
    if (!form || !/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    // Gift cards, personalised items and subscriptions keep the theme's own path
    if (form.querySelector('[name^="properties["], [name="selling_plan"]')) return;
    if (!(window.Shopify && window.Shopify.actions)) return;
    const data = new FormData(form);
    const variant = data.get('id');
    if (!variant) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.getAttribute('aria-busy') === 'true') return;

    const quantity = Math.max(1, parseInt(data.get('quantity') || '1', 10) || 1);
    button.setAttribute('aria-busy', 'true');
    button.classList.add('nafco-adding');
    burst(button);
    haptic();
    try {
      await cartUpdate([{ merchandiseId: `gid://shopify/ProductVariant/${variant}`, quantity }]);
      showToast({ title: titleFor(form), adding: quantity });
      button.classList.add('nafco-added');
      setTimeout(() => button.classList.remove('nafco-added'), 1400);
      closeQuickAdd(form);
    } catch (error) {
      showToast({ error: true, message: error && error.message });
    } finally {
      button.removeAttribute('aria-busy');
      button.classList.remove('nafco-adding');
      refreshCart(0);
    }
  }, true);

  function titleFor(form) {
    // Forms outside a product page (the hero-kit slider) name their product themselves
    if (form.dataset.nafcoTitle) return form.dataset.nafcoTitle;
    const scope = form.closest('.quick-add-modal, product-component, .product-information, main') || d;
    const heading = scope.querySelector('.product-details h1, h1, h2, .product-title');
    return heading ? heading.textContent.trim() : '';
  }

  function closeQuickAdd(form) {
    const host = form.closest('quick-add-dialog');
    if (!host) return;
    if (typeof host.closeDialog === 'function') host.closeDialog();
    else host.querySelector('dialog')?.close();
  }

  /* ── "Added to your cart" box ───────────────────────────────────────────── */

  let toastEl = null;
  let toastTimer = 0;

  const escapeHTML = (text) =>
    String(text || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  function friendlyError(message) {
    if (!message || /unavailable|busy|429|fetch|network|rejected|JSON/i.test(message)) {
      return 'The shop is busy for a moment. Please tap again.';
    }
    return message;
  }

  function showToast({ title = '', error = false, message = '', adding = 0 }) {
    if (!toastEl) {
      toastEl = d.createElement('div');
      toastEl.className = 'nafco-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      toastEl.addEventListener('click', () => {
        const wasError = toastEl.classList.contains('is-error');
        hideToast();
        if (!wasError) openCart();
      });
      d.body.appendChild(toastEl);
    }
    // Show the count the cart is about to have; the next cart read corrects it if needed
    const count = (cart ? cart.item_count : 0) + adding;
    toastEl.classList.toggle('is-error', error);
    toastEl.innerHTML = error
      ? '<span class="nafco-toast__icon" aria-hidden="true">!</span>' +
        `<span class="nafco-toast__text"><strong>Couldn’t add that</strong><span>${escapeHTML(friendlyError(message))}</span></span>`
      : '<span class="nafco-toast__icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.2 4.2L19 7"/></svg></span>' +
        `<span class="nafco-toast__text"><strong>Added to your cart</strong><span>${escapeHTML(title)}${title ? ' · ' : ''}<em data-nafco-toast-count>${count ? `${count} ${count === 1 ? 'item' : 'items'} in cart` : ''}</em></span></span>`;
    toastEl.classList.remove('is-out', 'is-in');
    void toastEl.offsetWidth;
    toastEl.classList.add('is-in');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, error ? 3400 : 2000);
  }

  function hideToast() {
    if (!toastEl) return;
    clearTimeout(toastTimer);
    toastEl.classList.remove('is-in');
    toastEl.classList.add('is-out');
  }

  /* ── "SALE" becomes the real saving, worked out from the two prices on the card ─ */

  const parseMoney = (text) => {
    const digits = (text || '').replace(/[^0-9.]/g, '');
    return digits ? parseFloat(digits) : NaN;
  };

  function labelDiscounts(scope = d) {
    for (const card of $$('product-card', scope)) {
      const badge = card.querySelector('.product-badges__badge');
      if (!badge || badge.dataset.nafcoOff) continue;
      const price = parseMoney(card.querySelector('product-price .price bdi')?.textContent);
      const compare = parseMoney(card.querySelector('product-price .compare-at-price bdi')?.textContent);
      if (!(price > 0 && compare > price)) continue;
      const off = Math.round((1 - price / compare) * 100);
      if (off < 1) continue;
      badge.dataset.nafcoOff = String(off);
      badge.textContent = `${off}% OFF`;
    }
  }

  /* ── hero ring ──────────────────────────────────────────────────────────── */

  function ringHeroes(scope = d) {
    for (const wrapper of $$('#MainContent .hero-wrapper', scope)) {
      if (wrapper.querySelector(':scope > .nafco-ring')) continue;
      const ring = d.createElement('div');
      ring.className = 'nafco-ring';
      ring.setAttribute('aria-hidden', 'true');
      ring.innerHTML = '<span class="nafco-ring__spin"></span>';
      const halo = d.createElement('div');
      halo.className = 'nafco-halo';
      halo.setAttribute('aria-hidden', 'true');
      halo.innerHTML = '<span class="nafco-ring__glow"></span>';
      wrapper.prepend(ring, halo);

      // Size the ring from the banner's real box: the wrapper's padding differs by section and screen
      const hero = wrapper.querySelector('.hero');
      const place = () => {
        if (!hero) return;
        const outer = wrapper.getBoundingClientRect();
        const box = hero.getBoundingClientRect();
        const width = matchMedia('(min-width: 750px)').matches ? 4 : 3;
        const radius = parseFloat(getComputedStyle(hero).borderTopLeftRadius) || 0;
        const top = box.top - outer.top;
        const left = box.left - outer.left;
        Object.assign(ring.style, {
          top: `${top - width}px`, left: `${left - width}px`,
          width: `${box.width + width * 2}px`, height: `${box.height + width * 2}px`,
          borderRadius: `${radius + width}px`,
        });
        Object.assign(halo.style, {
          top: `${top + 18}px`, left: `${left + 28}px`,
          width: `${Math.max(0, box.width - 56)}px`, height: `${Math.max(0, box.height - 18)}px`,
          borderRadius: `${radius}px`,
        });
      };
      place();
      if (hero && 'ResizeObserver' in window) new ResizeObserver(place).observe(hero);

      // Only spin while the banner is on screen
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          for (const entry of entries) ring.classList.toggle('nafco-ring--paused', !entry.isIntersecting);
        }).observe(wrapper);
      }
    }
  }

  /* ── reveal on scroll ───────────────────────────────────────────────────── */

  let revealObserver = null;

  function prepareReveal(scope = d) {
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const section = entry.target;
          section.classList.add('is-in');
          revealObserver.unobserve(section);
          // Drop the helper classes once the entrance has played, so nothing keeps a transform
          setTimeout(() => section.classList.remove('nafco-reveal', 'is-in'), 1800);
        }
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    }

    const viewportBottom = window.innerHeight;
    for (const section of $$('#MainContent > .shopify-section', scope)) {
      if (section.dataset.nafcoRevealed) continue;
      section.dataset.nafcoRevealed = '1';
      // Main product and collection sections stay put: they hold sticky parts
      if (section.querySelector('product-component, results-list, .product-information')) continue;
      if (section.getBoundingClientRect().top < viewportBottom * 0.92) continue;
      $$('product-card, .collection-card, .resource-list__item', section).forEach((item, index) => {
        item.style.setProperty('--i', String(index % 12));
      });
      section.classList.add('nafco-reveal');
      revealObserver.observe(section);
    }
  }

  /* ── pointer spotlight on cards (desktop) ───────────────────────────────── */

  if (finePointer && !reduceMotion) {
    let frame = 0;
    d.addEventListener('pointermove', (event) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const card = event.target.closest?.('product-card .product-card__content, .collection-card[data-nafco-collection]');
        if (!card) return;
        const box = card.getBoundingClientRect();
        card.style.setProperty('--mx', `${event.clientX - box.left}px`);
        card.style.setProperty('--my', `${event.clientY - box.top}px`);
      });
    }, { passive: true });
  }

  /* ── tap ripple ─────────────────────────────────────────────────────────── */

  const RIPPLE_HOSTS = '.button, .button-secondary, .quick-add__button, .nafco-cartbar, .nafco-search, .nafco-dock__tab';

  d.addEventListener('pointerdown', (event) => {
    if (reduceMotion || event.button !== 0) return;
    let host = event.target.closest(RIPPLE_HOSTS);
    if (!host || host.closest('.nafco-stepper')) return;
    if (host.classList.contains('nafco-dock__tab')) host = host.querySelector('.nafco-dock__icon') || host;
    const style = getComputedStyle(host);
    if (style.position === 'static') host.style.position = 'relative';
    if (style.overflow !== 'hidden') host.style.overflow = 'hidden';
    const box = host.getBoundingClientRect();
    const dot = d.createElement('span');
    dot.className = 'nafco-ripple';
    dot.style.left = `${event.clientX - box.left}px`;
    dot.style.top = `${event.clientY - box.top}px`;
    dot.style.setProperty('--s', String(Math.ceil(Math.hypot(box.width, box.height) / 6)));
    host.appendChild(dot);
    setTimeout(() => dot.remove(), 650);
  }, { passive: true });

  /* ── leaf burst and a tiny buzz when something goes into the cart (called from the cart code) ─ */

  const LEAF_COLOURS = ['#2f9e44', '#82c91e', '#20c997', '#f59f00', '#0d8a3b', '#94d82d'];

  function burst(origin) {
    if (reduceMotion || !origin) return;
    const box = origin.getBoundingClientRect();
    const layer = d.createElement('div');
    layer.className = 'nafco-burst';
    layer.style.left = `${box.left + box.width / 2}px`;
    layer.style.top = `${box.top + box.height / 2}px`;
    for (let i = 0; i < 9; i++) {
      const leaf = d.createElement('i');
      const angle = (Math.PI * 2 * i) / 9 + Math.random() * 0.5;
      const distance = 34 + Math.random() * 30;
      leaf.style.setProperty('--x', `${Math.cos(angle) * distance}px`);
      leaf.style.setProperty('--y', `${Math.sin(angle) * distance - 14}px`);
      leaf.style.setProperty('--r', `${Math.round(Math.random() * 360)}deg`);
      leaf.style.setProperty('--c', LEAF_COLOURS[i % LEAF_COLOURS.length]);
      layer.appendChild(leaf);
    }
    d.body.appendChild(layer);
    setTimeout(() => layer.remove(), 1000);
  }

  function haptic() {
    try { navigator.vibrate?.(12); } catch {}
  }

  /* ── the phone tab bar tucks away while you read and returns when you scroll up ─ */

  const dock = d.querySelector('[data-nafco-dock]');
  if (dock) {
    let lastY = 0;
    let ticking = false;
    const readY = (target) =>
      target === d || target === d.documentElement || target === d.body
        ? window.scrollY
        : target.scrollTop || 0;

    d.addEventListener('scroll', (event) => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const y = readY(event.target);
        const delta = y - lastY;
        if (Math.abs(delta) < 6) return;
        dock.classList.toggle('is-hidden', delta > 0 && y > 240);
        lastY = y;
      });
    }, { capture: true, passive: true });
  }

  /* ── page-load bar ──────────────────────────────────────────────────────── */

  const progress = d.querySelector('[data-nafco-progress]');
  if (progress) {
    d.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      progress.classList.add('is-loading');
    });
    window.addEventListener('pageshow', () => progress.classList.remove('is-loading'));
  }

  /* ── start, and re-run on theme-editor section reloads ──────────────────── */

  function decorate(scope = d) {
    labelDiscounts(scope);
    ringHeroes(scope);
    prepareReveal(scope);
    renderSteppers();
  }

  // Page-state classes for the stylesheet (cheaper for the browser than :has() on <html>/<body>)
  function watchPageState() {
    d.body.classList.toggle('nafco-has-dock', !!d.querySelector('[data-nafco-dock]'));
    const bar = d.querySelector('[data-nafco-cartbar]');
    d.body.classList.toggle('nafco-cartbar-on', !!bar && !bar.hidden);
    const dialogs = 'theme-drawer dialog[open], #search-modal dialog[open], dialog.quick-add-modal[open], .quick-add-modal[open]';
    const sync = () => d.documentElement.classList.toggle('nafco-dialog-open', !!d.querySelector(dialogs));
    sync();
    new MutationObserver(sync).observe(d.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
  }

  function start() {
    d.body.classList.add('nafco-app');
    watchPageState();
    startRotor();
    decorate();
    refreshCart(0);
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  d.addEventListener('shopify:section:load', (event) => decorate(event.target));

  // Product lists that load more cards later (filters, infinite scroll) get badges and steppers too
  if ('MutationObserver' in window) {
    let pending = 0;
    new MutationObserver(() => {
      if (pending) return;
      pending = requestAnimationFrame(() => {
        pending = 0;
        labelDiscounts();
        renderSteppers();
      });
    }).observe(d.getElementById('MainContent') || d.body, { childList: true, subtree: true });
  }
})();
